// frontend/packages/business-console/src/pages/WorkflowFormAuthor.jsx
//
// Work item 5.10 (PR6) — tenant self-serve AI form authoring.
//
// A businessadmin describes the form they want in plain language; the
// embedded agent proposes it (server-built prompt + compile/whitelist
// gate, per-tenant rate + spend caps — all enforced backend-side in
// 5.10 PR1–PR4). The admin previews the proposed fields, saves it as a
// draft, and submits it to the UpRyzze staff approval queue. Once staff
// approve, the form publishes and shows up under "Forms" for the team
// to fill.
//
// Chat + live preview only — NO raw JSX editor (5.10): the tenant never
// sees or edits the underlying source.
//
// `/business/:slug/forms/ai-builder`

import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';

import { BusinessContext } from '../contexts/BusinessContext';
import {
  authorWorkflowFormAiStream,
  createTenantDraft,
  listMyDrafts,
  submitDraftForApproval,
  deleteWorkflowForm,
} from '../services/workflowFormService';

function slugify(name) {
  return String(name || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);
}

function errMessage(err, fallback) {
  return err?.response?.data?.message || err?.message || fallback;
}

function fmtElapsed(sec) {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

// Group compiled fields by their enclosing <Section> (compile.js threads
// `section: { index, title }` per field, in document order — 5.16). Mirrors
// how WorkflowFormFill.jsx groups at fill time, so the preview reflects the
// real form's structure. Fields outside any section fall into a header-less
// group at the end.
function groupBySection(fields) {
  const byIndex = new Map();
  const groups = [];
  for (const f of fields) {
    const idx = f.section?.index ?? -1;
    let g = byIndex.get(idx);
    if (!g) {
      g = { index: idx, title: f.section?.title || '', fields: [] };
      byIndex.set(idx, g);
      groups.push(g);
    }
    g.fields.push(f);
  }
  groups.sort(
    (a, b) =>
      (a.index < 0 ? Infinity : a.index) - (b.index < 0 ? Infinity : b.index)
  );
  return groups;
}

const STATUS_COLOR = {
  draft: 'default',
  pending_approval: 'warning',
  approved: 'success',
  rejected: 'error',
};

const STATUS_LABEL = {
  draft: 'Draft',
  pending_approval: 'Pending review',
  approved: 'Approved',
  rejected: 'Changes requested',
};

function StatusChip({ status, publishedAt }) {
  if (publishedAt) {
    return <Chip size="small" color="success" label="Published" />;
  }
  return (
    <Chip
      size="small"
      color={STATUS_COLOR[status] || 'default'}
      label={STATUS_LABEL[status] || status || '—'}
    />
  );
}

export default function WorkflowFormAuthor() {
  const { business } = useContext(BusinessContext);
  const businessName = business?.businessName || business?.name || undefined;

  // Conversation + current proposal.
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);
  // Live chat reply as it streams in (5.10 live). While `thinking`, this
  // drives a token-by-token assistant bubble; `phase` swaps the status line
  // between the build and a compile-repair pass.
  const [streamingText, setStreamingText] = useState('');
  const [phase, setPhase] = useState(null);
  // Live "fields written so far" (from the stream's progress heartbeat) and an
  // elapsed-seconds timer — together they prove the build is moving during the
  // long, otherwise-silent jsxSource generation (the "did it lock up?" gap).
  const [buildFields, setBuildFields] = useState(0);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [proposal, setProposal] = useState(null);
  const [error, setError] = useState(null);

  // Per-session AI usage (tokens consumed toward this chat's budget). The
  // backend returns these on every author turn; we surface them so the
  // client can see what they're spending. (Account-wide totals / reset
  // date will get their own dashboard surface later.)
  const [usage, setUsage] = useState(null); // { sessionTokens, monthlyPct }

  // Auto-scroll the chat to the latest message.
  const messagesEndRef = useRef(null);

  // Save-as-draft.
  const [draftName, setDraftName] = useState('');
  const [draftSlug, setDraftSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedDraft, setSavedDraft] = useState(null);

  // My drafts list.
  const [drafts, setDrafts] = useState([]);
  const [draftsLoading, setDraftsLoading] = useState(true);
  const [busyDraftId, setBusyDraftId] = useState(null);

  // Delete-draft confirm dialog.
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const refreshDrafts = useCallback(async () => {
    setDraftsLoading(true);
    try {
      const res = await listMyDrafts();
      setDrafts(res.drafts || []);
    } catch (err) {
      // Non-fatal — the builder still works without the list.
      setDrafts([]);
    } finally {
      setDraftsLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshDrafts();
  }, [refreshDrafts]);

  // Keep the newest message in view as the conversation grows.
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, thinking, streamingText]);

  // Tick an elapsed timer while a build is in flight (reset between turns).
  useEffect(() => {
    if (!thinking) {
      setElapsedSec(0);
      return undefined;
    }
    setElapsedSec(0);
    const t = setInterval(() => setElapsedSec((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [thinking]);

  const monthlyPct =
    usage && typeof usage.monthlyPct === 'number'
      ? Math.min(100, Math.round(usage.monthlyPct))
      : null;

  // Keep the suggested slug in sync with the name until the user edits it.
  useEffect(() => {
    if (!slugTouched) setDraftSlug(slugify(draftName));
  }, [draftName, slugTouched]);

  const send = useCallback(async (overrideText) => {
    // `send` doubles as an onClick handler (which passes a DOM event), so
    // only honor a real string override (used by the one-click auto-fix).
    const text = (typeof overrideText === 'string' ? overrideText : input).trim();
    if (!text || thinking) return;
    setError(null);
    const nextMessages = [...messages, { role: 'user', content: text }];
    setMessages(nextMessages);
    setInput('');
    setThinking(true);
    setStreamingText('');
    setPhase('building');
    setBuildFields(0);
    try {
      const res = await authorWorkflowFormAiStream(
        {
          messages: nextMessages,
          currentJsx: proposal?.jsxSource,
          business: businessName ? { name: businessName } : undefined,
        },
        {
          onMessage: (text) => setStreamingText(text),
          onPhase: (p) => setPhase(p),
          onProgress: (n) => setBuildFields(n),
        }
      );
      setMessages((m) => [
        ...m,
        { role: 'assistant', content: res.message || '(no reply)' },
      ]);
      setProposal(res);
      // Accumulate tokens across the whole conversation (the backend's
      // `tokensUsed` is per-request — the runner is rebuilt each call — so
      // summing here gives the client an honest running session total).
      // `monthlyPct` is the capped resource (7.6 quota) and only arrives
      // once you're near the cap; keep the last value we saw.
      setUsage((u) => ({
        sessionTokens: (u?.sessionTokens || 0) + (res.tokensUsed || 0),
        monthlyPct:
          res.quotaWarning && typeof res.quotaWarning.pctOfCap === 'number'
            ? res.quotaWarning.pctOfCap
            : u?.monthlyPct ?? null,
      }));
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403) {
        setError('Only business admins can build forms with AI.');
      } else if (status === 429) {
        // Session budget used up, or monthly AI allowance reached. The
        // server message explains which and what to do next.
        setError(
          errMessage(
            err,
            "This chat has used up its AI budget. Open a new AI Form Builder chat to keep going; your saved drafts are safe."
          )
        );
      } else if (status === 422) {
        // Form too large to finish in one response: the server message
        // explains how to build it up a piece at a time.
        setError(
          errMessage(
            err,
            "That form was too big to finish at once. Start with the core sections, then add to it a piece at a time."
          )
        );
      } else {
        setError(errMessage(err, 'The AI builder hit a snag. Please retry.'));
      }
    } finally {
      setThinking(false);
      setStreamingText('');
      setPhase(null);
    }
  }, [input, thinking, messages, proposal, businessName]);

  // One-click "fix this": resend the current compile errors to the builder so
  // the operator doesn't have to hand-type them. The agent already receives
  // the broken `jsxSource` as `currentJsx`, so it repairs in place.
  const requestAutoFix = useCallback(() => {
    const errs = (proposal?.astErrors || [])
      .map((e) => `- ${e.message || e.code || String(e)}`)
      .join('\n');
    send(
      "The draft didn't compile. Please fix these errors and rebuild the " +
        'form, keeping everything else exactly the same:\n' +
        errs
    );
  }, [proposal, send]);

  const onSaveDraft = useCallback(async () => {
    if (!proposal?.compiled || !proposal?.jsxSource) return;
    const name = draftName.trim();
    const slug = (slugTouched ? draftSlug : slugify(draftName)).trim();
    if (!name) {
      setError('Give the form a name before saving.');
      return;
    }
    if (!slug) {
      setError('That name produced an empty URL slug — add some letters.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await createTenantDraft({
        slug,
        name,
        jsxSource: proposal.jsxSource,
      });
      setSavedDraft(res.draft);
      await refreshDrafts();
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) {
        setError(`A form with the URL slug "${slug}" already exists — pick another name.`);
      } else {
        setError(errMessage(err, "Couldn't save the draft."));
      }
    } finally {
      setSaving(false);
    }
  }, [proposal, draftName, draftSlug, slugTouched, refreshDrafts]);

  const onSubmitForApproval = useCallback(
    async (id) => {
      setBusyDraftId(id);
      setError(null);
      try {
        await submitDraftForApproval(id);
        setSavedDraft((d) => (d && d._id === id ? { ...d, approvalStatus: 'pending_approval' } : d));
        await refreshDrafts();
      } catch (err) {
        setError(errMessage(err, "Couldn't submit for approval."));
      } finally {
        setBusyDraftId(null);
      }
    },
    [refreshDrafts]
  );

  const confirmDeleteDraft = useCallback(async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setError(null);
    try {
      await deleteWorkflowForm(deleteTarget._id);
      setDrafts((prev) => prev.filter((d) => d._id !== deleteTarget._id));
      // If we just deleted the draft we'd saved in this session, reset the
      // save panel so the builder is ready for the next form.
      setSavedDraft((d) => (d && d._id === deleteTarget._id ? null : d));
      setDeleteTarget(null);
    } catch (err) {
      const status = err?.response?.status;
      setError(
        status === 403
          ? 'Only business admins can delete forms.'
          : errMessage(err, "Couldn't delete the draft.")
      );
    } finally {
      setDeleting(false);
    }
  }, [deleteTarget]);

  const fields =
    (proposal?.compiled &&
      proposal?.compiledSchema &&
      Array.isArray(proposal.compiledSchema.fields) &&
      proposal.compiledSchema.fields) ||
    [];
  const previewSections = groupBySection(fields);

  // Live status shown while a turn is in flight — fields-so-far + elapsed so
  // the long jsxSource stretch visibly moves instead of looking frozen.
  let buildStatusText;
  if (phase === 'repairing') {
    buildStatusText = `Fixing a couple of details… ${fmtElapsed(elapsedSec)} elapsed`;
  } else if (buildFields > 0) {
    buildStatusText = `Designing your form… ${buildFields} field${
      buildFields === 1 ? '' : 's'
    } so far · ${fmtElapsed(elapsedSec)} elapsed`;
  } else {
    buildStatusText = `Building your form… ${fmtElapsed(
      elapsedSec
    )} elapsed (a thorough one can take a minute or two)`;
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1100, mx: 'auto' }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
        <AutoAwesomeRoundedIcon color="primary" />
        <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: '-0.02em' }}>
          AI Form Builder
        </Typography>
      </Stack>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
        Tell the builder what the form is for — not sure exactly what you need
        is fine, it&apos;ll ask a few questions and design it with you. Then you
        review it and send it to the UpRyzze team for a quick approval before it
        goes live for your crew.
      </Typography>

      {usage ? (
        <Box sx={{ mb: 3, maxWidth: 360 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="baseline"
            sx={{ mb: monthlyPct != null ? 0.5 : 0 }}
          >
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
              AI usage this session
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {usage.sessionTokens.toLocaleString()} tokens
            </Typography>
          </Stack>
          {monthlyPct != null ? (
            <>
              <LinearProgress
                variant="determinate"
                value={monthlyPct}
                color={monthlyPct >= 90 ? 'warning' : 'primary'}
                sx={{ height: 6, borderRadius: 3 }}
              />
              <Typography
                variant="caption"
                sx={{ color: 'text.disabled', display: 'block', mt: 0.5 }}
              >
                {monthlyPct}% of this month&apos;s AI allowance used
              </Typography>
            </>
          ) : null}
        </Box>
      ) : null}

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 2,
          // On desktop, pin both panes to the viewport so each scrolls
          // internally instead of the whole page growing unbounded.
          alignItems: { md: 'start' },
        }}
      >
        {/* ── Chat column ── */}
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 360,
            height: { md: '70vh' },
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            Describe your form
          </Typography>
          <Stack spacing={1.5} sx={{ flex: 1, minHeight: 0, overflowY: 'auto', mb: 2, pr: 0.5 }}>
            {messages.length === 0 ? (
              <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                e.g. &quot;I need a form for tree health inspections&quot; — the
                builder will ask a couple of questions about who fills it and
                what you want to capture, then design it with you. Already know
                exactly what you want? Spell it out and it&apos;ll build it
                right away.
              </Typography>
            ) : (
              messages.map((m, i) => (
                <Box
                  key={i}
                  sx={{
                    alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                    maxWidth: '90%',
                    px: 1.5,
                    py: 1,
                    borderRadius: 2,
                    bgcolor: m.role === 'user' ? 'primary.main' : 'action.hover',
                    color: m.role === 'user' ? 'primary.contrastText' : 'text.primary',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  <Typography variant="body2">{m.content}</Typography>
                </Box>
              ))
            )}
            {thinking && streamingText ? (
              // Live assistant reply, streaming in token-by-token.
              <Box
                sx={{
                  alignSelf: 'flex-start',
                  maxWidth: '90%',
                  px: 1.5,
                  py: 1,
                  borderRadius: 2,
                  bgcolor: 'action.hover',
                  color: 'text.primary',
                  whiteSpace: 'pre-wrap',
                }}
              >
                <Typography variant="body2">
                  {streamingText}
                  <Box component="span" sx={{ opacity: 0.5 }}>
                    ▌
                  </Box>
                </Typography>
              </Box>
            ) : null}
            {thinking ? (
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ color: 'text.secondary' }}
              >
                <CircularProgress size={16} />
                <Typography variant="body2">{buildStatusText}</Typography>
              </Stack>
            ) : null}
            <div ref={messagesEndRef} />
          </Stack>
          <Stack direction="row" spacing={1}>
            <TextField
              fullWidth
              size="small"
              multiline
              maxRows={4}
              placeholder="Describe or refine the form…"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              disabled={thinking}
            />
            <Button
              variant="contained"
              onClick={send}
              disabled={thinking || !input.trim()}
              sx={{ minWidth: 0, px: 2 }}
            >
              <SendRoundedIcon fontSize="small" />
            </Button>
          </Stack>
        </Paper>

        {/* ── Preview column ── */}
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            borderRadius: 2,
            minHeight: 360,
            height: { md: '70vh' },
            overflowY: 'auto',
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            Preview
          </Typography>

          {!proposal ? (
            <Typography variant="body2" sx={{ color: 'text.disabled' }}>
              Your form will appear here once we&apos;ve worked out what it
              needs to capture.
            </Typography>
          ) : !proposal.jsxSource &&
            !(proposal.astErrors && proposal.astErrors.length) ? (
            // Discovery turn — the builder asked a question instead of
            // building. Not an error; point the user at the chat.
            <Alert severity="info">
              Check the chat — the builder has a couple of questions so it can
              design the right form for you.
            </Alert>
          ) : proposal.compiled === false ? (
            <Alert severity="warning">
              {proposal.astErrors && proposal.astErrors.length ? (
                <>
                  The draft didn&apos;t compile yet — I can fix it for you, or
                  you can adjust your request:
                  <Box
                    component="ul"
                    sx={{ m: '6px 0 0', pl: 2.5, fontSize: '0.85em' }}
                  >
                    {proposal.astErrors.slice(0, 4).map((e, i) => (
                      <li key={i}>{e.message || e.code || String(e)}</li>
                    ))}
                  </Box>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={requestAutoFix}
                    disabled={thinking}
                    sx={{ mt: 1 }}
                  >
                    {thinking ? 'Fixing…' : 'Fix this for me'}
                  </Button>
                </>
              ) : (
                "The draft isn't valid yet — keep describing what you need and I'll fix it."
              )}
            </Alert>
          ) : (
            <>
              {(proposal.warnings || []).length > 0 ? (
                <Alert severity="info" sx={{ mb: 1.5 }}>
                  {proposal.warnings.map((w, i) => (
                    <div key={i}>{w.message || String(w)}</div>
                  ))}
                </Alert>
              ) : null}
              {proposal.quotaWarning ? (
                <Alert severity="warning" sx={{ mb: 1.5 }}>
                  You&apos;ve used {proposal.quotaWarning.pctOfCap}% of this
                  month&apos;s AI allowance. When it reaches 100%, the AI Form
                  Builder pauses until the allowance resets next month (or your
                  limit is raised). Saved forms keep working.
                </Alert>
              ) : null}

              {fields.length === 0 ? (
                <Typography
                  variant="body2"
                  sx={{ color: 'text.secondary', mb: 2 }}
                >
                  No fields detected.
                </Typography>
              ) : (
                <>
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.disabled', display: 'block', mb: 1 }}
                  >
                    How the form is organized — fields marked{' '}
                    <Box component="span" sx={{ color: 'error.main' }}>
                      *
                    </Box>{' '}
                    are required; “Shows only when …” fields appear based on
                    earlier answers.
                  </Typography>
                  <Stack spacing={2} sx={{ mb: 2 }}>
                    {previewSections.map((sec) => (
                      <Box key={sec.index}>
                        {sec.title ? (
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 700, mb: 0.75 }}
                          >
                            {sec.title}
                          </Typography>
                        ) : null}
                        <Stack spacing={1}>
                          {sec.fields.map((f, i) => {
                            const fp = f.props || {};
                            const label = fp.label || f.path;
                            const options = Array.isArray(fp.options)
                              ? fp.options
                              : [];
                            return (
                              <Box
                                key={f.path || i}
                                sx={{
                                  px: 1.5,
                                  py: 1,
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  borderRadius: 1.5,
                                }}
                              >
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 1,
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600 }}
                                  >
                                    {label}
                                    {fp.required ? (
                                      <Box
                                        component="span"
                                        sx={{ color: 'error.main' }}
                                      >
                                        {' '}
                                        *
                                      </Box>
                                    ) : null}
                                  </Typography>
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    label={f.type || 'field'}
                                  />
                                </Box>
                                {fp.helperText ? (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: 'text.secondary',
                                      display: 'block',
                                      mt: 0.25,
                                    }}
                                  >
                                    {fp.helperText}
                                  </Typography>
                                ) : null}
                                {options.length > 0 ? (
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      flexWrap: 'wrap',
                                      gap: 0.5,
                                      mt: 0.5,
                                    }}
                                  >
                                    {options.slice(0, 8).map((o, oi) => (
                                      <Chip
                                        key={oi}
                                        size="small"
                                        label={o?.label ?? o?.value ?? String(o)}
                                        sx={{ height: 20 }}
                                      />
                                    ))}
                                    {options.length > 8 ? (
                                      <Typography
                                        variant="caption"
                                        sx={{ color: 'text.disabled' }}
                                      >
                                        +{options.length - 8} more
                                      </Typography>
                                    ) : null}
                                  </Box>
                                ) : null}
                                {fp.visibleWhen ? (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: 'info.main',
                                      display: 'block',
                                      mt: 0.5,
                                    }}
                                  >
                                    Shows only when: {fp.visibleWhen}
                                  </Typography>
                                ) : null}
                              </Box>
                            );
                          })}
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                </>
              )}

              <Divider sx={{ my: 1.5 }} />

              {savedDraft ? (
                <Stack spacing={1}>
                  <Alert severity="success">
                    Saved as “{savedDraft.name}”.
                  </Alert>
                  {savedDraft.approvalStatus === 'draft' ? (
                    <Button
                      variant="contained"
                      onClick={() => onSubmitForApproval(savedDraft._id)}
                      disabled={busyDraftId === savedDraft._id}
                    >
                      {busyDraftId === savedDraft._id ? 'Submitting…' : 'Submit for approval'}
                    </Button>
                  ) : (
                    <StatusChip status={savedDraft.approvalStatus} publishedAt={savedDraft.publishedAt} />
                  )}
                </Stack>
              ) : (
                <Stack spacing={1.5}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Save this form
                  </Typography>
                  <TextField
                    size="small"
                    label="Form name"
                    value={draftName}
                    onChange={(e) => setDraftName(e.target.value)}
                    fullWidth
                  />
                  <TextField
                    size="small"
                    label="URL slug"
                    value={draftSlug}
                    onChange={(e) => {
                      setSlugTouched(true);
                      setDraftSlug(slugify(e.target.value));
                    }}
                    helperText="Lowercase letters, numbers and dashes."
                    fullWidth
                  />
                  <Button
                    variant="contained"
                    onClick={onSaveDraft}
                    disabled={saving || !draftName.trim()}
                  >
                    {saving ? 'Saving…' : 'Save as draft'}
                  </Button>
                </Stack>
              )}
            </>
          )}
        </Paper>
      </Box>

      {/* ── My drafts ── */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
          My AI drafts
        </Typography>
        {draftsLoading ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <CircularProgress size={20} />
          </Box>
        ) : drafts.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Nothing yet — the forms you build above will land here with their
            approval status.
          </Typography>
        ) : (
          <Stack spacing={1}>
            {drafts.map((d) => (
              <Paper
                key={d._id}
                variant="outlined"
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  flexWrap: 'wrap',
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {d.name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                    <code>{d.slug}</code> · v{d.version}
                  </Typography>
                  {d.approvalStatus === 'rejected' && d.approvalFeedback ? (
                    <Typography variant="caption" sx={{ display: 'block', color: 'error.main', mt: 0.5 }}>
                      Feedback: {d.approvalFeedback}
                    </Typography>
                  ) : null}
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <StatusChip status={d.approvalStatus} publishedAt={d.publishedAt} />
                  {!d.publishedAt &&
                  (d.approvalStatus === 'draft' || d.approvalStatus === 'rejected') ? (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => onSubmitForApproval(d._id)}
                      disabled={busyDraftId === d._id}
                    >
                      {busyDraftId === d._id ? 'Submitting…' : 'Submit for approval'}
                    </Button>
                  ) : null}
                  <Tooltip title="Delete draft">
                    <IconButton
                      size="small"
                      aria-label={`Delete ${d.name}`}
                      onClick={() => setDeleteTarget(d)}
                      sx={{ color: 'text.secondary' }}
                    >
                      <DeleteOutlineRoundedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Paper>
            ))}
          </Stack>
        )}
      </Box>

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => (deleting ? null : setDeleteTarget(null))}
      >
        <DialogTitle>Delete this draft?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            “{deleteTarget?.name}” will be removed. If it was already
            published, your team will no longer see it; submissions already
            filled out are kept. UpRyzze support can restore it if needed.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button color="error" onClick={confirmDeleteDraft} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
